import type { CSSProperties, ReactElement } from 'react'
import thumbDownRedRaw from './assets/thumb-down-red.svg?raw'
import thumbDownBlackRaw from './assets/thumb-down-black.svg?raw'
import finishRaw from './assets/finish.svg?raw'
import type { FeedbackIconName } from './types'

/**
 * Feedback illustration icons.
 *
 * `assets/*.svg` are the cross-platform exported assets, copied 1:1 from
 * the iOS Feedback asset catalog
 * (`MetroModules/Sources/Feedback/Assets.xcassets`) — NOT hand-drawn.
 * They are full-color illustrations (a circular thumb-down for the form
 * header, a finish badge for ThankYou) with a baked drop-shadow filter,
 * so they do NOT use `currentColor` and do not theme.
 *
 * Inlined via `?raw` + `dangerouslySetInnerHTML` (matching
 * `@metro/suggester` `figmaIcons.tsx`) so the SVG renders inline at a
 * fixed box size. The markup is a trusted build-time constant — no user
 * input — so inlining is safe.
 */
function rawIllustration(raw: string, size: number): ReactElement {
  const style: CSSProperties = {
    display: 'inline-flex',
    width: size,
    height: size,
  }
  return <span aria-hidden style={style} dangerouslySetInnerHTML={{ __html: raw }} />
}

/** Form-header icon — selected by the configuration's `iconName`. */
export function FeedbackHeaderIcon({
  name,
  size = 148,
}: {
  name: FeedbackIconName
  size?: number
}): ReactElement {
  const raw = name === 'thumbDownRed' ? thumbDownRedRaw : thumbDownBlackRaw
  return rawIllustration(raw, size)
}

/** ThankYou success badge (iOS `feedback.finish`). */
export function FeedbackFinishIcon({ size = 148 }: { size?: number }): ReactElement {
  return rawIllustration(finishRaw, size)
}
